import React from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { Button } from '@pandiafi/design-system'
import { useNavigate } from 'react-router-dom'

import { ResidentAppError } from '../../../common/error'
import { ResidentAppErrorCode } from '../../../common/types'

import { Error } from 'components/Error'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'

interface ContractModificationErrorHandlerProps {
  children: React.ReactNode
  view: 'form' | 'confirm'
}

export const ContractModificationErrorHandler: React.FC<
  ContractModificationErrorHandlerProps
> = ({ children, view }) => {
  const [t] = useTranslation(['contractModification'])
  const navigate = useNavigate()
  const { terminateContractPath, confirmContractModificationPath } =
    useContractPaths()

  const fallbackRender = ({ error, resetErrorBoundary }: FallbackProps) => {
    const restartPath =
      view === 'form' ? terminateContractPath : confirmContractModificationPath

    const RestartButton = (
      <Button
        onClick={() => {
          resetErrorBoundary()
          navigate(restartPath, { replace: true })
        }}
      >
        {t('buttons.restart')}
      </Button>
    )

    const title =
      view === 'form'
        ? t('errors.formErrorTitle')
        : t('errors.confirmErrorTitle')

    if (error instanceof ResidentAppError) {
      switch (error.errorCode) {
        case ResidentAppErrorCode.CONFIRM_OPERATION_GENERIC_ERROR:
          return <Error title={title} action={RestartButton} />

        case ResidentAppErrorCode.CONFIRM_OPERATION_CANCELLED:
          return (
            <Error
              title={title}
              message={t('confirmationError.cancelled')}
              illustration="house-confused"
              action={RestartButton}
            />
          )

        case ResidentAppErrorCode.CONFIRM_OPERATION_IDENTITY_MISMATCH:
          return (
            <Error
              title={title}
              message={t('confirmationError.identityMismatch')}
              action={RestartButton}
            />
          )

        case ResidentAppErrorCode.CONFIRM_OPERATION_NO_ATTRIBUTES:
          return (
            <Error
              title={title}
              message={t('confirmationError.noAttributes')}
            />
          )

        case ResidentAppErrorCode.CONFIRM_OPERATION_NO_IDENTITY:
          return (
            <Error
              title={title}
              message={t('confirmationError.noIdentity')}
              action={RestartButton}
            />
          )

        case ResidentAppErrorCode.CONFIRM_OPERATION_NO_IDENTITY_VERIFIER:
          return (
            <Error
              title={title}
              message={t('confirmationError.noIdentityVerifier')}
              action={RestartButton}
            />
          )

        case ResidentAppErrorCode.CONFIRM_OPERATION_NO_USER:
          return (
            <Error
              title={title}
              message={t('confirmationError.noUser')}
              action={RestartButton}
            />
          )

        case ResidentAppErrorCode.MODIFICATION_CONTRACT_INIT_CONFIRM_ERROR:
        case ResidentAppErrorCode.MODIFICATION_CONTRACT_CONFIRM_ERROR:
          return (
            <Error
              title={title}
              message={t('confirmationError.initFailed')}
              action={RestartButton}
            />
          )

        case ResidentAppErrorCode.ADD_TENANT_INIT_CONFIRM_ERROR:
          return (
            <Error
              title={title}
              message={t('confirmationError.addTenantInitFailed')}
              action={RestartButton}
            />
          )

        default:
      }
    }

    return <Error message={t('errors.outOfService')} />
  }

  return (
    <ErrorBoundary fallbackRender={fallbackRender}>{children}</ErrorBoundary>
  )
}
